/*
 * Detects when a newer build has been deployed than the one this page is running.
 *
 * Why this exists: an installed PWA (and any long-lived tab) keeps its window
 * alive. Tapping the home-screen icon RESUMES the old window — it does not
 * navigate — so the `no-store` on index.html never gets a chance to pull the new
 * build, and because the chunks already in memory keep working there is no
 * ChunkLoadError to trigger the boundary's reload. The user silently stays on
 * stale code until they kill the app. See docs/chunk-load-error-playbook.md.
 *
 * How: CRA writes the running main bundle into index.html as
 * `<script src="/static/js/main.<hash>.js">` and the deployed one into
 * asset-manifest.json as `files["main.js"]`. The hash changes on every deploy
 * that changes JS, and server.js serves asset-manifest.json with `no-store`, so
 * comparing the two is a cheap, cache-proof "is there a newer build?" check.
 *
 * This deliberately does NOT touch the service worker or Cache Storage — the
 * worker stays inert (public/service-worker.js). It is only a detector.
 */

const MANIFEST_URL = "/asset-manifest.json";

// The main bundle this page actually booted with, taken from the DOM.
export const getRunningMainBundle = () => {
  const script = document.querySelector('script[src*="/static/js/main."]');
  if (!script?.src) return null;
  try {
    return new URL(script.src, window.location.origin).pathname;
  } catch (error) {
    return null;
  }
};

// The main bundle currently on the server, or null if it cannot be determined
// (offline, proxy error, malformed manifest) — callers must treat null as
// "unknown", never as "changed".
export const fetchDeployedMainBundle = async () => {
  // The query string defeats any intermediate cache that ignores `no-store`.
  const response = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, {
    cache: "no-store",
    credentials: "same-origin",
  });
  if (!response.ok) return null;

  const contentType = response.headers.get("content-type") || "";
  // The express catch-all answers unknown paths with index.html (HTTP 200),
  // so a non-JSON body means the manifest is missing, not that it changed.
  if (!contentType.includes("json")) return null;

  const manifest = await response.json();
  const main = manifest?.files?.["main.js"];
  if (typeof main !== "string" || !main) return null;
  try {
    return new URL(main, window.location.origin).pathname;
  } catch (error) {
    return null;
  }
};

export const isNewBuildAvailable = async () => {
  const running = getRunningMainBundle();
  if (!running) return false;
  const deployed = await fetchDeployedMainBundle();
  return !!deployed && deployed !== running;
};
